/**
 * Material Issue Notes (MIN) and Material Return Notes (MRN).
 *
 * The two documents are mirror images of one another — MIN takes stock out to
 * the RCM, MRN puts good-quality unused stock back — so they share one screen
 * driven by a `mode` prop rather than two near-identical copies.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, PackageMinus, Pencil, Plus, Send, Trash2 } from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Select, StatusPill, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import {
  AttachmentCollector, AttachmentList, AuditTrail, MaterialPicker, NotPermitted,
  queueAttachments, type PendingAttachment,
} from '@/components/docs/shared'
import { DocumentActions } from '@/components/docs/DocumentActions'
import {
  TimberTally, blankTallyRow, isTimber, tallyCft, tallyFromJson, tallyToJson,
  type TallyRow,
} from '@/components/docs/TimberTally'
import { useAuth } from '@/lib/auth'
import { DocumentDate, todayIso } from '@/components/docs/DocumentDate'
import { DeleteDocument } from '@/components/docs/DeleteDocument'
import {
  fetchLines, fetchOne, issuedAgainstIndent, stockOnHand, useMaterials, usePeople, useProfiles,
  useQuery, useSiteNames, useSites, useStock,
} from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { can, isEditable, isHoRole, iso, seesManySites, visibleSites } from '@/lib/domain'
import { ddmmyyyy, qty } from '@/lib/format'
import { enqueue, reviseDocument } from '@/lib/sync'
import type { Material } from '@/lib/types'

type Mode = 'MIN' | 'MRN'

const CONFIG = {
  MIN: {
    table: 'material_issue_notes',
    lineTable: 'min_line_items',
    fk: 'min_id',
    route: '/mins',
    title: 'Material Issue Notes',
    subtitle: 'Material handed to the Resident Construction Manager for site work',
    newTitle: 'Issue material to the site',
    newSubtitle: 'Stock leaves your store only after the Head of Stores approves',
    qtyField: 'quantity_issued',
    dateField: 'issue_date',
    qtyLabel: 'Quantity issued',
    capability: 'createMin',
    kind: 'min.create' as const,
    approveNote: 'Approval deducts these quantities from site stock.',
  },
  MRN: {
    table: 'material_return_notes',
    lineTable: 'mrn_line_items',
    fk: 'mrn_id',
    route: '/mrns',
    title: 'Material Return Notes',
    subtitle: 'Good-quality unused material coming back from the RCM',
    newTitle: 'Record material returned from site',
    newSubtitle: 'Only good-quality material may be returned to stock',
    qtyField: 'quantity_returned',
    dateField: 'return_date',
    qtyLabel: 'Quantity returned',
    capability: 'createMrn',
    kind: 'mrn.create' as const,
    approveNote: 'Approval adds these quantities back to site stock.',
  },
} as const

// ---------------------------------------------------------------------------
export function StoreDocList({ mode }: { mode: Mode }) {
  const cfg = CONFIG[mode]
  const { profile } = useAuth()
  // Names, not email addresses — management 01-09-2026. See src/lib/people.ts.
  const people = usePeople()
  const role = profile!.role
  const ho = seesManySites(role)
  // A Project Manager picking a colleague's site would see nothing, which
  // looks like a fault rather than a boundary. Offer him only his own.
  const [site, setSite] = useState(ho ? '' : (profile!.site_id ?? ''))
  const [status, setStatus] = useState('')

  const { data: sites } = useSites()
  const { siteName } = useSiteNames()
  const mySites = useMemo(() => visibleSites(sites, role, profile!.id), [sites, role, profile])
  const { data, loading, error } = useQuery<any>(cfg.table, () => {
    let q = supabase.from(cfg.table).select('*')
    if (site) q = q.eq('site_id', site)
    if (status) q = q.eq('status', status)
    return q.order(cfg.dateField, { ascending: false }).limit(500)
  }, [site, status])

  const { sort, toggle } = useSort(cfg.dateField)
  // The Created by column shows a name; sorting it on the hidden email would
  // reorder the table by something the reader cannot see.
  const named = useMemo(
    () => (data as any[]).map((d) => ({ ...d, created_by_name: people.name({ email: d.creator_email }) })),
    [data, people.index])
  const sorted = useMemo(() => sortRows(named, sort, {
    [cfg.dateField]: 'date',
  }), [named, sort])

  return (
    <div className="space-y-4">
      <Card
        title={cfg.title}
        subtitle={cfg.subtitle}
        actions={can(role, cfg.capability) && (
          <Link to={`${cfg.route}/new`} className="btn-primary btn-sm">
            <Plus className="h-4 w-4" /> New {mode}
          </Link>
        )}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {ho && (
            <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                    options={mySites.map((s) => ({ value: s.id, label: s.site_name }))} />
          )}
          <Select
            value={status} onChange={(e) => setStatus(e.target.value)} placeholder="All statuses"
            options={[
              { value: 'SUBMITTED', label: 'Awaiting approval' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'RETURNED', label: 'Returned for revision' },
              { value: 'REJECTED', label: 'Rejected' },
            ]}
          />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load" message={error} /></div>}
        {!loading && !error && data.length === 0 && (
          <EmptyState icon={<PackageMinus className="h-9 w-9" />} title={`No ${mode}s yet`} />
        )}
        {data.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>{mode}</Th>
                  <Th id="site_id" sort={sort} onSort={toggle}>Site</Th>
                  <Th id={cfg.dateField} sort={sort} onSort={toggle}>Date</Th>
                  <Th id={mode === 'MIN' ? 'issued_to_name' : 'returned_by_name'} sort={sort} onSort={toggle}>
                    {mode === 'MIN' ? 'Issued to' : 'Returned by'}
                  </Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  <Th id="created_by_name" sort={sort} onSort={toggle}>Created by</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((d: any) => (
                  <tr key={d.id}>
                    <td>
                      <Link to={`${cfg.route}/${d.id}`} className="font-mono text-xs font-semibold text-oval-700 hover:underline">
                        {d.id}
                      </Link>
                    </td>
                    <td className="text-xs" title={d.site_id}>{siteName(d.site_id)}</td>
                    <td className="text-xs">{ddmmyyyy(d[cfg.dateField])}</td>
                    <td className="text-xs">{mode === 'MIN' ? d.issued_to_name : d.returned_by_name}</td>
                    <td><StatusPill status={d.status} /></td>
                    <td className="max-w-[180px] truncate text-xs text-slate-500">{d.created_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
interface Line {
  key: string
  material_id: string | null
  quantity: string
  remarks: string
  on_hand: number | null
  /** What the RCM's indent asked for on this material, when issuing against one. */
  asked: number | null
  /** The timber tally sheet, for materials that are measured rather than counted. */
  tally: TallyRow[]
}
const blank = (): Line => ({
  key: crypto.randomUUID(), material_id: null, quantity: '', remarks: '', on_hand: null, asked: null,
  tally: [],
})

export function StoreDocNew({ mode }: { mode: Mode }) {
  const cfg = CONFIG[mode]
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()
  const { id: editId } = useParams()
  const editing = !!editId
  const [loadingDoc, setLoadingDoc] = useState(editing)
  // A form that failed to load must not be offered for editing.
  const [editBlocked, setEditBlocked] = useState(false)

  const role = profile!.role
  const ho = isHoRole(role)
  const { data: sites } = useSites()
  const { data: materials } = useMaterials()
  const { data: people } = useProfiles()

  const [siteId, setSiteId] = useState(profile!.site_id ?? 'HO')
  const [docDate, setDocDate] = useState(iso(new Date()))
  const [counterparty, setCounterparty] = useState('')
  const [work, setWork] = useState('')
  const [remarks, setRemarks] = useState('')
  // MIN only: the RCM's indent this issue satisfies, if any.
  const [sourceIndent, setSourceIndent] = useState(params.get('from_indent') ?? '')
  const [openIndents, setOpenIndents] = useState<Array<{ id: string; work_description: string | null; required_by: string | null }>>([])
  const [lines, setLines] = useState<Line[]>([blank()])
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const errorRef = useRef<HTMLDivElement>(null)

  const { data: stock } = useStock(siteId)

  /*
   * Stock on the ISSUE DATE, not today.
   *
   * This screen used to check the quantity against today's balance. On a
   * back-dated note that is the wrong question in both directions: it blocked
   * an issue of material the site genuinely held back then and has since used,
   * and it waved through an issue of material that had not yet arrived — which
   * is how a site ended up at minus 4,000 bricks for a month.
   *
   * `blockNegative` is the administrator's switch, in Master Data -> settings.
   * Off while the backlog is loading: warn, allow, and record. On afterwards:
   * refuse. The database applies exactly the same rule, so this is only here
   * to tell the user before they waste a submission.
   */
  const [asAtStock, setAsAtStock] = useState<Record<string, number> | null>(null)
  const [blockNegative, setBlockNegative] = useState(false)
  const historic = docDate < todayIso()

  useEffect(() => {
    supabase.from('company_profile').select('block_negative_stock').eq('id', 1).maybeSingle()
      .then(({ data }) => setBlockNegative(!!data?.block_negative_stock))
  }, [])

  useEffect(() => {
    if (!historic || !siteId) { setAsAtStock(null); return }
    let alive = true
    void (async () => {
      const { data } = await supabase.rpc('rpc_stock_as_at',
        { p_as_at: docDate, p_site_id: siteId })
      if (!alive) return
      // Materials with nil balance are omitted by the snapshot, so anything
      // missing is genuinely zero on that date.
      setAsAtStock(Object.fromEntries(
        ((data ?? []) as any[])
          .filter((r) => r.bucket === 'SITE')
          .map((r) => [r.material_id, Number(r.quantity)])))
    })()
    return () => { alive = false }
  }, [siteId, docDate, historic])

  /** What the site held on the document's own date. */
  const stockOn = (materialId: string): number =>
    historic
      ? (asAtStock?.[materialId] ?? 0)
      : (stock.find((s) => s.material_id === materialId)?.quantity_on_hand ?? 0)

  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])

  // The RCM is the counterparty for both documents.
  const rcms = people.filter((p) => p.site_id === siteId &&
    ['site_construction_manager', 'storeman', 'store_executive'].includes(p.role))

  // Indents still untouched on this site — an issue note can close one outright.
  // Deliberately SUBMITTED only: an indent reaches ACCEPTED because part of it
  // has already been issued, and the store gets one bite at each indent.
  useEffect(() => {
    if (mode !== 'MIN' || !siteId) return
    void (async () => {
      const { data } = await supabase.from('indents')
        .select('id, work_description, required_by')
        .eq('site_id', siteId).eq('status', 'SUBMITTED')
        .order('indent_date', { ascending: false })
      setOpenIndents((data ?? []) as any)
    })()
  }, [mode, siteId])

  /**
   * Issuing against an indent — pull the RCM's list straight in.
   *
   * The store-man asked for this material to be issued; making him retype the
   * list the RCM already typed is how issues get missed. Every line comes
   * across with what was asked for and what is actually on the shelf, so he can
   * see at a glance what he can meet and what has to go on a requisition.
   */
  useEffect(() => {
    if (mode !== 'MIN' || editing) return
    const fromIndent = params.get('from_indent')
    if (!fromIndent) return

    void (async () => {
      setLoadingDoc(true)
      try {
        const header = await fetchOne<any>('indents', fromIndent)
        const rows = await fetchLines<any>('indent_line_items', 'indent_id', fromIndent)
        if (!header) throw new Error(`Indent ${fromIndent} was not found.`)

        const site = header.site_id
        setSiteId(site)
        setSourceIndent(fromIndent)
        if (header.work_description) setWork(header.work_description)
        setRemarks(`Issued against site indent ${fromIndent}`)

        // Anything already issued against this indent is settled — only the
        // balance is still open, even if somebody reaches this screen directly.
        const alreadyIssued = await issuedAgainstIndent(fromIndent)
        const remaining = rows
          .map((r: any) => ({ r, asked: Math.max(0, Number(r.quantity) - (alreadyIssued[r.material_id] ?? 0)) }))
          .filter(({ asked }) => asked > 0.001)

        if (rows.length && remaining.length === 0) {
          setError('Everything on this indent has already been issued from the store.')
        } else if (remaining.length) {
          setLines(await Promise.all(remaining.map(async ({ r, asked }) => {
            const onHand = r.material_id ? await stockOnHand(site, r.material_id) : null
            return {
              key: crypto.randomUUID(),
              material_id: r.material_id,
              // Never pre-fill more than is actually on the shelf — the whole
              // point of this screen is what he can genuinely hand over today.
              // Timber is never pre-filled with a volume: the store-man must
              // count the pieces he is actually handing over, and the sheet
              // will foot them. Everything else keeps its head start.
              quantity: isTimber(byId[r.material_id])
                ? ''
                : String(onHand === null ? asked : Math.min(asked, onHand)),
              remarks: r.remarks ?? '',
              on_hand: onHand,
              asked,
              tally: isTimber(byId[r.material_id]) ? [blankTallyRow(byId[r.material_id])] : [],
            }
          })))
        }
        setError(null)
      } catch (e) {
        setError(e instanceof Error
          ? `Could not load the indent (${e.message}). You can still enter the issue note by hand.`
          : 'Could not load the indent. You can still enter the issue note by hand.')
      } finally {
        setLoadingDoc(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editing])

  // Correcting a note the Head of Stores sent back.
  useEffect(() => {
    if (!editId) return
    void (async () => {
      setLoadingDoc(true)
      try {
        const { error: guard } = await supabase.rpc('rpc_assert_editable', {
          p_doc_type: mode, p_doc_id: editId,
        })
        if (guard) throw new Error(guard.message)

        const h = await fetchOne<any>(cfg.table, editId)
        if (!h) throw new Error(`${mode} ${editId} was not found.`)
        const rows = await fetchLines<any>(cfg.lineTable, cfg.fk, editId)

        setSiteId(h.site_id)
        setDocDate(h[cfg.dateField] ?? iso(new Date()))
        setCounterparty((mode === 'MIN' ? h.issued_to : h.returned_by) ?? '')
        setWork(h.work_description ?? '')
        setRemarks(h.remarks ?? '')
        setSourceIndent(h.source_indent_id ?? '')
        setLines((rows ?? []).map((r: any) => ({
          key: crypto.randomUUID(),
          material_id: r.material_id,
          quantity: String(r[cfg.qtyField] ?? ''),
          on_hand: r.stock_at_issue ?? null,
          remarks: r.remarks ?? '',
          asked: null,
          tally: tallyFromJson(r.timber_tally),
        })))
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : `Could not open this ${mode} for correction`)
        setEditBlocked(true)
      } finally { setLoadingDoc(false) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, mode])

  if (!can(role, cfg.capability)) {
    return <NotPermitted what={`create a ${mode}`} who="site Store-man (or the HO Stores Executive)" />
  }

  function update(key: string, patch: Partial<Line>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  // Move the date and every line's "in stock" figure has to move with it.
  useEffect(() => {
    setLines((ls) => ls.map((l) => (l.material_id
      ? { ...l, on_hand: stockOn(l.material_id) }
      : l)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asAtStock, historic, siteId, stock.length])

  function pick(key: string, m: Material | null) {
    const onHand = m ? stockOn(m.id) : null
    // Changing the material by hand detaches the line from whatever the indent asked for.
    update(key, {
      material_id: m?.id ?? null, on_hand: onHand, asked: null,
      // Timber opens its sheet straight away, cross-section already filled in.
      tally: isTimber(m) ? [blankTallyRow(m)] : [],
      quantity: '',
    })
  }

  /**
   * On measured timber the quantity is the footed tally, never a typed number.
   * One place where it changes, so the sheet and the box cannot disagree.
   */
  function setTally(key: string, rows: TallyRow[]) {
    update(key, { tally: rows, quantity: rows.length ? String(tallyCft(rows)) : '' })
  }

  function validate(send: boolean): string | null {
    const filled = lines.filter((l) => l.material_id && Number(l.quantity) > 0)
    if (filled.length === 0) return 'Add at least one material with a quantity.'
    // A draft is allowed to be incomplete. Only a note actually going to the
    // Head of Stores has to name a person and stand up to the stock check.
    if (!send) return null
    if (!counterparty) return mode === 'MIN' ? 'Say who you are issuing the material to.' : 'Say who returned the material.'
    // Over-issue is checked against the ISSUE DATE, and only refused when the
    // administrator has turned blocking on. While the backlog is loading it is
    // allowed and recorded, because paperwork arrives out of order.
    if (mode === 'MIN' && blockNegative) {
      const short = filled.find((l) => l.on_hand !== null && Number(l.quantity) > l.on_hand + 0.001)
      if (short) {
        const m = byId[short.material_id!]
        return `${m?.item_name} — only ${qty(short.on_hand!)} ${m?.unit_of_measure} was in stock at this site on ${ddmmyyyy(docDate)}, so ${short.quantity} cannot be issued. Enter the receipt that supplied it first, or correct the date.`
      }
    }
    if (mode === 'MIN' && !work.trim()) return 'Describe the work the material is for.'
    return null
  }

  async function submit(send = true) {
    const problem = validate(send)
    if (problem) {
      setError(problem)
      // Take him to it. The Submit button sits at the foot of a form that can
      // run to several screens, so an error drawn at the top is, from where he
      // is standing, nothing happening at all. Reported on the goods receipt
      // screen 09-09-2026; the same shape of form, the same trap.
      requestAnimationFrame(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
      return
    }
    setError(null); setBusy(true)
    try {
      const filled = lines.filter((l) => l.material_id && Number(l.quantity) > 0)
      const person = people.find((p) => p.id === counterparty)
      const header: any = {
        site_id: siteId,
        status: send ? 'SUBMITTED' : 'DRAFT',
        [cfg.dateField]: docDate,
        created_by: profile!.id,
        creator_email: profile!.email,
        remarks: remarks || null,
      }
      if (mode === 'MIN') {
        header.issued_to = counterparty
        header.issued_to_name = person?.full_name ?? null
        header.work_description = work
        header.source_indent_id = sourceIndent || null
      } else {
        header.returned_by = counterparty
        header.returned_by_name = person?.full_name ?? null
      }

      const lineRows = filled.map((l, i) => ({
        sl_no: i + 1,
        material_id: l.material_id,
        uom: byId[l.material_id!]?.unit_of_measure ?? 'NO',
        [cfg.qtyField]: Number(l.quantity),
        timber_tally: tallyToJson(l.tally),
        ...(mode === 'MIN' ? { stock_at_issue: l.on_hand } : { material_condition: 'Good' }),
        remarks: l.remarks || null,
      }))

      // ---- correcting a returned note --------------------------------------
      if (editing) {
        const { status: _s, created_by: _c, creator_email: _e, site_id: _si, ...editableHeader } = header
        await reviseDocument({
          docType: mode,
          docId: editId!,
          header: editableHeader,
          lines: lineRows,
          submit: true,
          comment: 'Corrected and resubmitted after being returned',
        })
        if (attachments.length) await queueAttachments(mode, editId!, attachments)
        toast.success(`Corrected ${mode} sent back to the Head of Stores.`)
        navigate(`${cfg.route}/${editId}`)
        return
      }

      const localId = `${mode}-LOCAL-${Date.now()}`
      await enqueue({
        kind: cfg.kind, docType: mode,
        localDocId: localId,
        label: `${send ? mode : `Draft ${mode}`} at ${siteId} — ${filled.length} material(s)`,
        payload: { header, lines: lineRows },
      })
      if (attachments.length) await queueAttachments(mode, localId, attachments)

      toast.success(!send
        ? `Saved as a draft ${mode}. Nothing has gone to the Head of Stores and no stock has moved.`
        : navigator.onLine
          ? `${mode} submitted to the Head of Stores.`
          : `Saved on this device. It uploads when you are back online.`)
      navigate(cfg.route)
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not save the ${mode}`)
    } finally { setBusy(false) }
  }

  if (loadingDoc) return <Loading label={`Opening the ${mode}…`} />

  if (editBlocked) {
    return (
      <div className="space-y-4">
        <Alert tone="error" title={`This ${mode} cannot be corrected`}>{error}</Alert>
        <button className="btn-secondary" onClick={() => navigate(`${cfg.route}/${editId}`)}>
          Back to the document
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card
        title={editing ? `Correct ${mode} ${editId}` : cfg.newTitle}
        subtitle={editing
          ? 'Make the changes the Head of Stores asked for, then send it back. The note keeps its number and its history.'
          : cfg.newSubtitle}
      >
        <div ref={errorRef}>{error && <div className="mb-4"><Alert tone="error">{error}</Alert></div>}</div>
        {mode === 'MIN' && !editing && sourceIndent && lines.some((l) => l.asked !== null) && (
          <div className="mb-4">
            <Alert tone="info" title={`Issuing against indent ${sourceIndent}`}>
              Every material the RCM asked for is listed below, pre-filled with what you can
              actually hand over today. Where you hold less than was asked for, the line shows the
              shortfall — issue what you have, then raise a purchase requisition for the balance.
              The indent closes by itself only once every line has been issued in full.
            </Alert>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Site" required>
            <Select value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={!ho || editing}
                    options={sites.map((s) => ({ value: s.id, label: `${s.site_name} (${s.site_code})` }))} />
          </Field>
          <DocumentDate label={mode === 'MIN' ? 'Issued on' : 'Returned on'}
                        value={docDate} onChange={setDocDate}
                        hint="The day the material actually moved. Stock moves on this date." />
          <Field label={mode === 'MIN' ? 'Issue to' : 'Returned by'} required className="lg:col-span-2">
            <Select
              value={counterparty} onChange={(e) => setCounterparty(e.target.value)}
              placeholder="Select the person…"
              options={rcms.map((p) => ({ value: p.id, label: `${p.full_name} — ${p.designation ?? p.role}` }))}
            />
          </Field>
          {mode === 'MIN' && (
            <Field
              label="Against site indent"
              className="sm:col-span-2 lg:col-span-2"
              hint="Optional. If the whole indent is met from your own store, choosing it here closes it — no requisition, no purchase order."
            >
              <Select
                value={sourceIndent}
                onChange={(e) => setSourceIndent(e.target.value)}
                placeholder="Not against an indent"
                options={[
                  ...(sourceIndent && !openIndents.some((i) => i.id === sourceIndent)
                    ? [{ value: sourceIndent, label: sourceIndent }] : []),
                  ...openIndents.map((i) => ({
                    value: i.id,
                    label: `${i.id}${i.work_description ? ` — ${i.work_description}` : ''}`,
                  })),
                ]}
              />
            </Field>
          )}
          {mode === 'MIN' && (
            <Field label="Work this material is for" required className="sm:col-span-2 lg:col-span-4">
              <Input value={work} onChange={(e) => setWork(e.target.value)}
                     placeholder="e.g. Column shuttering, grid B3–B7, ground floor" />
            </Field>
          )}
        </div>
      </Card>

      <Card
        title="Materials"
        subtitle={mode === 'MIN' ? 'Your live stock is shown so you cannot over-issue' : 'Only good-quality material can be returned to stock'}
        actions={<button className="btn-secondary btn-sm" onClick={() => /* prepend: the blank row appears at the top so nobody scrolls to the bottom of a long document to type the next line. Agreed 09-09-2026. */ setLines((l) => [blank(), ...l])}>
          <Plus className="h-4 w-4" /> Add line
        </button>}
      >
        <div className="space-y-3">
          {lines.map((l, i) => {
            const m = l.material_id ? byId[l.material_id] : null
            const over = mode === 'MIN' && l.on_hand !== null && Number(l.quantity) > l.on_hand + 0.001
            return (
              <div key={l.key} className={`rounded-lg border p-3 ${over ? 'border-red-300 bg-red-50/40' : 'border-slate-200'}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-400">LINE {i + 1}</span>
                  {lines.length > 1 && (
                    <button className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                            onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="grid gap-3 lg:grid-cols-12">
                  <Field label="Material" required className="lg:col-span-5">
                    <MaterialPicker
                      value={l.material_id} onChange={(m) => pick(l.key, m)} autoFocus={false}
                      exclude={lines.filter((x) => x.key !== l.key).map((x) => x.material_id!).filter(Boolean)}
                    />
                    {mode === 'MIN' && l.on_hand !== null && m && (
                      <p className={`hint ${over ? (blockNegative ? 'font-semibold text-red-600' : 'font-semibold text-amber-700') : 'text-emerald-700'}`}>
                        In stock{historic ? ` on ${ddmmyyyy(docDate)}` : ''}: {qty(l.on_hand)} {m.unit_of_measure}
                        {over && (blockNegative
                          ? ' — you cannot issue more than the site held on that date'
                          : ' — more than the site held on that date. Allowed, but it will be recorded as a stock warning.')}
                      </p>
                    )}
                    {mode === 'MIN' && l.asked !== null && m && (
                      <p className="hint text-slate-500">
                        Indent asked for {qty(l.asked)} {m.unit_of_measure}
                        {l.on_hand !== null && l.asked > l.on_hand && (
                          <span className="font-semibold text-amber-700">
                            {' '}· {qty(l.asked - l.on_hand)} short — raise a requisition for the balance
                          </span>
                        )}
                      </p>
                    )}
                  </Field>
                  <Field label={`${cfg.qtyLabel}${m ? ` (${m.unit_of_measure})` : ''}`} required className="lg:col-span-3"
                         hint={isTimber(m) ? 'Footed from the tally sheet below' : undefined}>
                    <Input type="number" inputMode="decimal" min="0" step="any" invalid={over}
                           value={l.quantity} onChange={(e) => update(l.key, { quantity: e.target.value })}
                           readOnly={isTimber(m)}
                           title={isTimber(m)
                             ? 'On measured timber this is the total of the tally sheet below. Change a length there.'
                             : undefined}
                           className={isTimber(m) ? 'cursor-not-allowed bg-amber-50 font-semibold' : ''} />
                  </Field>
                  <Field label="Remarks" className="lg:col-span-4">
                    <Input value={l.remarks} onChange={(e) => update(l.key, { remarks: e.target.value })} />
                  </Field>
                </div>

                {isTimber(m) && m && (
                  <div className="mt-3">
                    <TimberTally material={m} rows={l.tally}
                                 onChange={(rows) => setTally(l.key, rows)} />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="mt-4">
          <Field label="Remarks">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Field>
        </div>
      </Card>

      <Card
        title="Supporting documents"
        subtitle={mode === 'MIN'
          ? "The RCM's written request, a gate pass, a photograph of what left the store"
          : 'A photograph of the material coming back, and anything explaining its condition'}
      >
        <AttachmentCollector docType={mode} value={attachments} onChange={setAttachments} />
        {editing && (
          <p className="mt-2 text-xs text-slate-500">
            Files already on this note stay where they are. Anything you add here is added to them.
          </p>
        )}
      </Card>

      <div className="sticky bottom-20 z-10 flex justify-end gap-2 lg:bottom-4">
        <button className="btn-ghost" onClick={() => navigate(cfg.route)} disabled={busy}>Cancel</button>
        {!editing && (
          <button className="btn-secondary" onClick={() => submit(false)} disabled={busy}>
            Save as draft
          </button>
        )}
        <button className="btn-primary" onClick={() => submit(true)} disabled={busy}>
          <Send className="h-4 w-4" /> Submit to Head of Stores
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
export function StoreDocDetail({ mode }: { mode: Mode }) {
  const cfg = CONFIG[mode]
  const { id = '' } = useParams()
  const { profile } = useAuth()
  // Names, not email addresses — management 01-09-2026. See src/lib/people.ts.
  const people = usePeople()
  // Site names, not SITE-TRP-05 — same reasoning, 05-09-2026.
  const { siteName } = useSiteNames()
  const [doc, setDoc] = useState<any>(null)
  const [lines, setLines] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { data: materials } = useMaterials()
  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const header = await fetchOne<any>(cfg.table, id)
      if (!header) throw new Error(`${mode} ${id} was not found, or you do not have access to it.`)
      setDoc(header)
      setLines(await fetchLines<any>(cfg.lineTable, cfg.fk, id))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the document')
    } finally { setLoading(false) }
  }, [id, cfg, mode])

  useEffect(() => { void load() }, [load])

  if (loading) return <Loading />
  if (error || !doc) return <Alert tone="error">{error}</Alert>

  const canCorrect = isEditable(doc.status) && (doc.creator_email === profile!.email || profile!.role === 'admin')

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link to={cfg.route} className="btn-ghost btn-sm"><ArrowLeft className="h-4 w-4" /> All {mode}s</Link>
          <h1 className="font-mono text-base font-bold text-slate-800">{doc.id}</h1>
          <StatusPill status={doc.status} />
        </div>
        <div className="flex flex-wrap gap-2">
          {canCorrect && (
            <Link to={`${cfg.route}/${doc.id}/edit`} className="btn-primary btn-sm">
              <Pencil className="h-4 w-4" /> Correct and resubmit
            </Link>
          )}
          <DeleteDocument docType={mode} docId={doc.id} status={doc.status}
                          creatorEmail={doc.creator_email} backTo={cfg.route} />
        </div>
      </div>

      {doc.status === 'APPROVED' && (
        <Alert tone="success" title="Approved — inventory updated">
          {mode === 'MIN' ? 'These quantities have been deducted from' : 'These quantities have been added back to'}{' '}
          the {siteName(doc.site_id)} inventory.
        </Alert>
      )}
      {doc.status === 'REJECTED' && (
        <Alert tone="error" title="Rejected — permanently stopped">{doc.rejection_reason ?? 'No reason recorded.'}</Alert>
      )}
      {doc.status === 'RETURNED' && (
        <Alert tone="warning" title="Returned for revision">{doc.return_reason ?? 'No note recorded.'}</Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Details" className="lg:col-span-2">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
            <D label="Site" value={doc.site_id} />
            <D label="Date" value={ddmmyyyy(doc[cfg.dateField])} />
            <D label={mode === 'MIN' ? 'Issued to' : 'Returned by'}
               value={(mode === 'MIN' ? doc.issued_to_name : doc.returned_by_name) ?? '—'} />
            <D label="Created by" value={people.name({ email: doc.creator_email })} />
            <D label="Approved by" value={doc.approver_email ? people.name({ email: doc.approver_email }) : '—'} />
            {mode === 'MIN' && <D label="Work" value={doc.work_description ?? '—'} />}
            {mode === 'MIN' && doc.source_indent_id && (
              <div>
                <dt className="text-xs font-semibold text-slate-500">Against site indent</dt>
                <dd className="mt-0.5">
                  <Link to={`/indents/${doc.source_indent_id}`}
                        className="font-mono text-xs text-oval-700 hover:underline">
                    {doc.source_indent_id}
                  </Link>
                </dd>
              </div>
            )}
          </dl>
          {mode === 'MIN' && doc.source_indent_id && doc.status === 'APPROVED' && (
            <div className="mt-4">
              <Alert tone="info">
                This issue closed indent {doc.source_indent_id}. It was met entirely from site stock,
                so it never became a requisition.
              </Alert>
            </div>
          )}
        </Card>
        <Card title="Decision" subtitle={cfg.approveNote}>
          <DocumentActions docType={mode} docId={doc.id} status={doc.status} onDone={load} className="flex flex-col gap-2" />
          {doc.status !== 'SUBMITTED' && <p className="text-sm text-slate-500">No decision is pending.</p>}
        </Card>
      </div>

      <Card title="Materials">
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr><th>#</th><th>Material</th><th>Code</th><th className="num">{cfg.qtyLabel}</th><th>Remarks</th></tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const m = byId[l.material_id]
                return (
                  <tr key={l.id}>
                    <td className="text-xs text-slate-400">{l.sl_no}</td>
                    <td className="text-sm font-medium">{m?.item_name ?? l.material_id}</td>
                    <td className="font-mono text-xs text-slate-500">{m?.item_code ?? '—'}</td>
                    <td className="num font-semibold">{qty(l[cfg.qtyField])} <span className="text-xs text-slate-400">{l.uom}</span></td>
                    <td className="text-xs text-slate-500">{l.remarks ?? '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <AttachmentList docType={mode} docId={doc.id}
                      subtitle="Anything the site attached to this note" />

      <AuditTrail docType={mode} docId={doc.id} />
    </div>
  )
}

const D = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs font-semibold text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-slate-800">{value}</dd>
  </div>
)
